import { FC, useMemo } from 'react';

import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'lib/highcharts';
import { useTranslation } from 'react-i18next';

import { Baseplan, PlannedHour } from 'components/types';

import { BLUE } from 'constants/colors';

interface PlannedHoursChartProps {
  baseplans: Baseplan[];
  plannedHours: PlannedHour[];
  year: number;
}

const PlannedHoursChart: FC<PlannedHoursChartProps> = ({
  baseplans,
  plannedHours,
  year
}) => {
  const { t } = useTranslation('common');

  const options = useMemo(() => {
    const yAxis = baseplans.filter(({ baseplanId }) =>
      plannedHours.some(({ baseplanId: b }) => baseplanId === b)
    );

    const series = [
      {
        name: t('baseplan.name'),
        borderColor: 'gray',
        color: BLUE,
        colorByPoint: false,
        pointWidth: 20,
        tooltip: {
          xDateFormat: '%e %B %Y'
        },
        data: plannedHours.map(({ baseplanId: b, startDate }, i) => ({
          x: Date.parse(startDate),
          x2: plannedHours[i + 1]
            ? Date.parse(plannedHours[i + 1].startDate)
            : Date.UTC(year, 11, 31),
          y: yAxis.findIndex(({ baseplanId }) => baseplanId === b)
        }))
      }
    ];

    const options = {
      title: {
        text: ''
      },
      time: {
        getTimezoneOffset(timestamp) {
          const timezoneOffset = new Date(timestamp).getTimezoneOffset();

          return timezoneOffset;
        }
      },
      chart: {
        type: 'xrange',
        height: `${plannedHours.length > 1 ? yAxis.length * 42 : 68}px`
      },
      legend: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      xAxis: {
        type: 'datetime'
      },
      yAxis: {
        title: '',
        categories: yAxis.map(({ name }) => name),
        reversed: true
      },
      series
    };

    return options;
  }, [baseplans, plannedHours, year]);

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default PlannedHoursChart;
